/**
 * Per-message Content Encryption Key (CEK) lifecycle.
 *
 * The CEK is a 32-byte AES-256-GCM key generated on the creator's device at
 * seal time. It is double-wrapped before it leaves the device:
 *
 *   1. cek_recipient_wrap = HPKE(recipient_pubkey, CEK, info=message_id)
 *   2. cek_vault_wrap     = AES-256-GCM(vault_DEK, CEK, nonce=HKDF(message_id))
 *
 * Both ciphertexts are stored on the server; the CEK plaintext never is.
 * The recipient unwraps via path (1) on their device. Path (2) is the
 * guardian-mediated continuity path: if the recipient cannot be reached,
 * a quorum of guardians can reconstruct the vault DEK and decrypt the
 * envelope, then re-wrap the CEK to a different recipient public key. The
 * server never participates in (1); it only participates in (2) by holding
 * the vault DEK in memory during a deliberate, audited continuity flow.
 *
 * See: docs/crypto/ENCRYPTION-ARCHITECTURE.md §4 (seal flow), §5 (delivery),
 *      §7 (guardian continuity).
 *      docs/decisions/ADR-002-encryption-model.md commitments 2 & 3.
 *      docs/durability/CRYPTO-AGILITY-PROTOCOL.md §6.2 (migration path).
 *
 * IMPORTANT: This module is NOT a server-side decryptor of message content.
 * It exposes one server-callable function — `unwrapCekFromVaultDek` — that
 * is *only* used during continuity activation, when a quorum has already
 * authorised vault DEK reconstruction. Routine server code should NEVER
 * import that function. See `index.ts` barrel for the recommended surface.
 */
import { BindingMismatchError, InvalidKeyMaterialError, WrongKeyError } from './errors.js';
import { open as hpkeOpen, seal as hpkeSeal } from './hpke.js';
import { ACTIVE_SUITE, parseSuitePrefixed, prefixWithSuite, SuiteUnknownError, } from './suite.js';
/**
 * AES-256 key length in bytes.
 */
export const CEK_LENGTH_BYTES = 32;
/**
 * AES-256-GCM nonce length in bytes (NIST SP 800-38D §5.2.1.1 recommends 12).
 */
const GCM_NONCE_BYTES = 12;
/**
 * AES-256-GCM authentication tag length in bytes.
 */
const GCM_TAG_BYTES = 16;
/**
 * Vault DEK length: AES-256, 32 bytes. Same primitive as the CEK; different
 * scope (one DEK per vault, regenerated only on owner-driven re-key).
 */
const VAULT_DEK_BYTES = 32;
/**
 * KEM enc length for DHKEM(X25519): a fixed 32 bytes. We exploit this to
 * pack `enc || ct` into a single base64url payload behind the suite prefix
 * so each wrapped CEK is a single string in the database.
 */
const HPKE_ENC_BYTES = 32;
// ---------------------------------------------------------------------------
// CEK generation
// ---------------------------------------------------------------------------
/**
 * Generate a fresh 32-byte CEK using `crypto.getRandomValues` (CSPRNG).
 *
 * The CEK is intended to live in device memory only. Callers should
 * minimise the lifetime: generate, encrypt the blob, wrap to recipient
 * pubkey + vault DEK, then zeroize. Zeroization is best-effort in
 * JavaScript — the V8 engine may have copies — but a careful caller
 * still drops the only live reference promptly.
 */
export function generateCek() {
    return randomBytes(CEK_LENGTH_BYTES);
}
// ---------------------------------------------------------------------------
// Wrap / unwrap to recipient pubkey (path 1: HPKE)
// ---------------------------------------------------------------------------
/**
 * Wrap a CEK to a recipient's X25519 public key, returning the canonical
 * suite-prefixed serialized form ready to store in
 * `messages.cek_recipient_wrap`.
 *
 * The `info` parameter binds the ciphertext to the message id so a
 * server-side adversary cannot transplant a CEK envelope from one message
 * to another.
 *
 * @param cek - 32-byte AES-256-GCM key (from {@link generateCek}).
 * @param recipientPubKey - imported X25519 public key (from
 *   {@link keys.importPublicKeyFromServer}).
 * @param messageId - the UUID of the messages row this CEK belongs to.
 *
 * @returns 'v1:hpke-x25519-sha256-aes256gcm:<base64url(enc || ct)>'.
 */
export async function wrapCekForRecipient(cek, recipientPubKey, messageId) {
    assertCekLength(cek);
    assertNonEmptyMessageId(messageId);
    const info = encodeMessageId(messageId);
    const { enc, ct } = await hpkeSeal(recipientPubKey, cek, info);
    if (enc.byteLength !== HPKE_ENC_BYTES) {
        throw new InvalidKeyMaterialError('HPKE enc', HPKE_ENC_BYTES, enc.byteLength);
    }
    const payload = concatBytes(enc, ct);
    return prefixWithSuite(ACTIVE_SUITE, payload);
}
/**
 * Unwrap a `cek_recipient_wrap` envelope on the recipient's device.
 *
 * @param wrappedCek - the suite-prefixed envelope as stored in the DB.
 * @param recipientPrivKey - the recipient's device-bound X25519 private key.
 * @param messageId - the same messages.id used at seal time. Mismatch raises
 *   {@link BindingMismatchError}.
 *
 * @returns the 32-byte AES-256-GCM CEK plaintext, ready to decrypt the
 *   content blob with {@link aesGcmDecrypt} (or whatever the caller's
 *   blob-handling code does).
 */
export async function unwrapCekForRecipient(wrappedCek, recipientPrivKey, messageId) {
    assertNonEmptyMessageId(messageId);
    const { suiteId, payload } = parseSuitePrefixed(wrappedCek);
    if (suiteId !== ACTIVE_SUITE) {
        throw new SuiteUnknownError(suiteId, `cek_recipient_wrap suite mismatch (active='${ACTIVE_SUITE}')`);
    }
    const { enc, ct } = splitEncAndCt(payload);
    const info = encodeMessageId(messageId);
    const cek = await hpkeOpen(recipientPrivKey, enc, ct, info);
    if (cek.byteLength !== CEK_LENGTH_BYTES) {
        throw new InvalidKeyMaterialError('CEK plaintext', CEK_LENGTH_BYTES, cek.byteLength);
    }
    return cek;
}
// ---------------------------------------------------------------------------
// Wrap / unwrap to vault DEK (path 2: AES-256-GCM, continuity-only)
// ---------------------------------------------------------------------------
/**
 * Wrap a CEK under the vault DEK. This is the second of the two CEK wraps
 * the creator produces at seal time; it sits dormant in the DB and is only
 * touched during a guardian-mediated continuity flow.
 *
 * AES-256-GCM nonce derivation is deterministic per-message — we use
 * HKDF-Expand over `messageId || 'la-cek-vault-wrap-nonce'` so that:
 *   (a) the same message always re-encrypts to the same envelope (idempotent
 *       re-wrap during crypto migration), and
 *   (b) two different messages never collide on a nonce, even if the same
 *       vault DEK is in use.
 *
 * Because the vault DEK is rotated only when the owner re-keys (a rare,
 * audited event), this gives us nonce-uniqueness across the entire vault
 * lifetime without requiring a per-vault nonce counter.
 *
 * @param cek - 32-byte AES-256-GCM CEK plaintext.
 * @param vaultDek - 32-byte AES-256-GCM vault DEK (held only in device or
 *   audited-server memory).
 * @param messageId - the messages.id this CEK belongs to.
 */
export async function wrapCekForVaultDek(cek, vaultDek, messageId) {
    assertCekLength(cek);
    assertVaultDekLength(vaultDek);
    assertNonEmptyMessageId(messageId);
    const nonce = await deriveGcmNonce(vaultDek, messageId);
    const ct = await aesGcmEncrypt(vaultDek, nonce, cek, encodeMessageId(messageId));
    // Pack as nonce || ct(includes 16-byte tag) so the unwrap side can recover
    // both without an external schema. Total size: 12 + 32 + 16 = 60 bytes.
    const payload = concatBytes(nonce, ct);
    return prefixWithSuite(ACTIVE_SUITE, payload);
}
/**
 * Unwrap a `cek_vault_wrap` envelope. Used during continuity activation
 * (guardian quorum or Trust trustee) — never during routine delivery.
 *
 * Server code MUST gate calls to this function behind the continuity
 * authorisation check (see BACKEND-ARCHITECTURE.md trustee-role guard).
 * The function itself does not enforce that gate; it only does crypto.
 *
 * @throws SuiteUnknownError if the envelope was not produced under the
 *   active suite (caller should trigger migration to the active suite).
 * @throws WrongKeyError if the vault DEK does not match the envelope.
 * @throws BindingMismatchError if the message id does not match the
 *   envelope's nonce derivation.
 */
export async function unwrapCekFromVaultDek(wrappedCek, vaultDek, messageId) {
    assertVaultDekLength(vaultDek);
    assertNonEmptyMessageId(messageId);
    const { suiteId, payload } = parseSuitePrefixed(wrappedCek);
    if (suiteId !== ACTIVE_SUITE) {
        throw new SuiteUnknownError(suiteId, `cek_vault_wrap suite mismatch (active='${ACTIVE_SUITE}')`);
    }
    if (payload.byteLength < GCM_NONCE_BYTES + GCM_TAG_BYTES) {
        throw new InvalidKeyMaterialError('cek_vault_wrap payload', GCM_NONCE_BYTES + CEK_LENGTH_BYTES + GCM_TAG_BYTES, payload.byteLength);
    }
    const storedNonce = payload.subarray(0, GCM_NONCE_BYTES);
    const ct = payload.subarray(GCM_NONCE_BYTES);
    // Re-derive the expected nonce. If it does not match, the envelope was
    // sealed against a different message id (transplant attempt or DB bug).
    const expectedNonce = await deriveGcmNonce(vaultDek, messageId);
    if (!constantTimeEquals(storedNonce, expectedNonce)) {
        throw new BindingMismatchError(messageId);
    }
    let plaintext;
    try {
        plaintext = await aesGcmDecrypt(vaultDek, storedNonce, ct, encodeMessageId(messageId));
    }
    catch (err) {
        throw new WrongKeyError('AES-256-GCM open against vault DEK', err);
    }
    if (plaintext.byteLength !== CEK_LENGTH_BYTES) {
        throw new InvalidKeyMaterialError('CEK plaintext', CEK_LENGTH_BYTES, plaintext.byteLength);
    }
    return plaintext;
}
// ---------------------------------------------------------------------------
// Internal helpers (AES-GCM, HKDF, parsing)
// ---------------------------------------------------------------------------
/**
 * Get a SubtleCrypto reference. This module is server-only at runtime, but
 * we go through `globalThis.crypto.subtle` so the same source compiles for
 * both Node and browser builds.
 */
function getSubtle() {
    const c = globalThis.crypto;
    if (c === undefined || c.subtle === undefined) {
        throw new Error('globalThis.crypto.subtle is unavailable; Node 20+ or modern browser required');
    }
    return c.subtle;
}
/**
 * Pull cryptographically-strong random bytes via `crypto.getRandomValues`.
 */
function randomBytes(n) {
    const c = globalThis.crypto;
    if (c === undefined || typeof c.getRandomValues !== 'function') {
        throw new Error('globalThis.crypto.getRandomValues is unavailable');
    }
    const out = new Uint8Array(n);
    c.getRandomValues(out);
    return out;
}
/**
 * Derive a deterministic 12-byte AES-GCM nonce from (vault DEK, message id).
 * HKDF-Expand-only mode using the vault DEK as the PRK.
 *
 * Domain-separation salt 'la-cek-vault-wrap-nonce-v1' is mixed in so this
 * derivation cannot collide with any other HKDF use of the same vault DEK.
 */
async function deriveGcmNonce(vaultDek, messageId) {
    const subtle = getSubtle();
    const info = new TextEncoder().encode(`la-cek-vault-wrap-nonce-v1:${messageId}`);
    // WebCrypto HKDF (RFC 5869), HKDF-Expand-only with the vault DEK as PRK.
    // Identical output to Node's `hkdf('sha256', dek, emptySalt, info, len)` —
    // an empty salt is treated as HashLen zero bytes by both. Using SubtleCrypto
    // here (instead of node:crypto) keeps this module portable to the browser,
    // where the recipient unwraps on-device with the exact same source.
    const baseKey = await subtle.importKey('raw', toBufferSource(vaultDek), 'HKDF', false, [
        'deriveBits',
    ]);
    const derived = await subtle.deriveBits({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: toBufferSource(new Uint8Array(0)),
        info: toBufferSource(info),
    }, baseKey, GCM_NONCE_BYTES * 8);
    return new Uint8Array(derived);
}
/**
 * Encrypt with AES-256-GCM via WebCrypto. AAD is the message id, providing
 * an extra binding layer alongside the deterministic nonce.
 */
async function aesGcmEncrypt(key, nonce, plaintext, aad) {
    const subtle = getSubtle();
    const cryptoKey = await subtle.importKey('raw', toBufferSource(key), { name: 'AES-GCM' }, false, ['encrypt']);
    const ct = await subtle.encrypt({ name: 'AES-GCM', iv: toBufferSource(nonce), additionalData: toBufferSource(aad), tagLength: 128 }, cryptoKey, toBufferSource(plaintext));
    return new Uint8Array(ct);
}
/**
 * Decrypt with AES-256-GCM via WebCrypto.
 */
async function aesGcmDecrypt(key, nonce, ciphertext, aad) {
    const subtle = getSubtle();
    const cryptoKey = await subtle.importKey('raw', toBufferSource(key), { name: 'AES-GCM' }, false, ['decrypt']);
    const pt = await subtle.decrypt({ name: 'AES-GCM', iv: toBufferSource(nonce), additionalData: toBufferSource(aad), tagLength: 128 }, cryptoKey, toBufferSource(ciphertext));
    return new Uint8Array(pt);
}
/**
 * Coerce a Uint8Array to a fresh ArrayBuffer-backed BufferSource. Newer
 * TypeScript DOM lib types tighten BufferSource to require ArrayBuffer
 * (not ArrayBufferLike, which includes SharedArrayBuffer); copying into a
 * tight new Uint8Array satisfies this without runtime cost worth caring about.
 */
function toBufferSource(view) {
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
}
function encodeMessageId(messageId) {
    return new TextEncoder().encode(messageId);
}
function concatBytes(a, b) {
    const out = new Uint8Array(a.byteLength + b.byteLength);
    out.set(a, 0);
    out.set(b, a.byteLength);
    return out;
}
function splitEncAndCt(payload) {
    if (payload.byteLength <= HPKE_ENC_BYTES) {
        throw new InvalidKeyMaterialError('HPKE wrapped CEK payload', HPKE_ENC_BYTES + 1, payload.byteLength);
    }
    return {
        enc: payload.subarray(0, HPKE_ENC_BYTES),
        ct: payload.subarray(HPKE_ENC_BYTES),
    };
}
function constantTimeEquals(a, b) {
    if (a.byteLength !== b.byteLength)
        return false;
    let diff = 0;
    for (let i = 0; i < a.byteLength; i++) {
        diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
    }
    return diff === 0;
}
function assertCekLength(cek) {
    if (cek.byteLength !== CEK_LENGTH_BYTES) {
        throw new InvalidKeyMaterialError('CEK', CEK_LENGTH_BYTES, cek.byteLength);
    }
}
function assertVaultDekLength(vaultDek) {
    if (vaultDek.byteLength !== VAULT_DEK_BYTES) {
        throw new InvalidKeyMaterialError('vault DEK', VAULT_DEK_BYTES, vaultDek.byteLength);
    }
}
function assertNonEmptyMessageId(messageId) {
    if (typeof messageId !== 'string' || messageId.length === 0) {
        throw new InvalidKeyMaterialError('messageId', 1, 0);
    }
}
